import {
  Container,
  Group,
  Title,
  Card,
  Badge,
  Text,
  Switch,
  Stack,
  Checkbox,
  Alert,
  List,
  Anchor,
  Divider,
  Paper,
} from '@mantine/core';
import { IconMessage, IconInfoCircle, IconLock } from '@tabler/icons-react';

/**
 * Public, read-only preview of the authenticated SMS opt-in screen.
 *
 * This page exists specifically so that carrier campaign reviewers (A2P 10DLC / TCR)
 * can verify the end-user SMS consent flow without needing to authenticate into the
 * customer portal. It mirrors the UI that existing B2B customers see at
 * /account/sms-preferences when they choose to opt in via the web portal.
 *
 * Nothing on this page actually enrolls the viewer — it is a static preview only.
 */
export default function SMSOptInPreview() {
  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Alert icon={<IconLock size={16} />} color="gray" variant="light">
          <Text size="sm" fw={600} mb={4}>Preview / Consent Flow Documentation</Text>
          <Text size="sm">
            This page is a public, read-only preview of the SMS opt-in screen that
            authenticated Standard Produce customers see at{' '}
            <Text component="span" ff="monospace">portal.standardproduce.com/account/sms-preferences</Text>.
            It is published so that carrier and messaging-platform reviewers can verify
            our A2P 10DLC consent flow without logging in. No action on this page will
            subscribe you to SMS. To actually opt in, existing B2B customers must log in
            to their account, review and acknowledge the terms, and enable the toggle.
          </Text>
        </Alert>

        <Group>
          <IconMessage size={28} />
          <Title order={2}>SMS Notifications (Preview)</Title>
        </Group>

        <Paper withBorder p="md" bg="gray.0">
          <Text size="xs" c="dimmed" fw={600} mb="xs">CONSENT IS OBTAINED BY:</Text>
          <List size="sm" spacing={4}>
            <List.Item>Explicit checkbox acknowledgment of SMS Terms and Privacy Policy</List.Item>
            <List.Item>Clear display of message frequency and "msg &amp; data rates may apply"</List.Item>
            <List.Item>Sample message shown before opt-in</List.Item>
            <List.Item>Timestamp, IP address, and full consent text recorded server-side</List.Item>
            <List.Item>Opt-in toggle is disabled until the checkbox is checked</List.Item>
          </List>
        </Paper>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600} size="lg">Current Status</Text>
                <Text size="sm" c="dimmed">Manage your SMS notification preferences</Text>
              </div>
              <Badge color="red" variant="light" size="lg">
                Opted Out
              </Badge>
            </Group>

            <Text size="sm">
              <Text component="span" fw={600}>Phone Number:</Text> (XXX) XXX-XXXX (shown from
              authenticated account profile)
            </Text>

            <Divider />

            <Stack gap="xs">
              <Text fw={600} size="sm">Program Details</Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Text component="span" fw={500}>Program:</Text> Standard Produce Customer Care SMS
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Message types:</Text> Order confirmations,
                  delivery ETAs, account balance / invoice alerts, and responses to SMS commands
                  (ORDER, BAL, PAY, HELP).
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Message frequency:</Text> Varies based on your
                  ordering activity — typically 2-10 messages per week for active accounts.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Cost:</Text> Message and data rates may apply.
                  Check with your mobile carrier for details.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Opt out:</Text> Reply <strong>STOP</strong> to
                  any message at any time. Reply <strong>HELP</strong> for help or call
                  (434) 204-2184.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Privacy:</Text> Mobile numbers and SMS opt-in
                  data are never shared with third parties or affiliates for marketing or
                  promotional purposes.
                </List.Item>
              </List>
            </Stack>

            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              <Text size="sm" fw={600} mb={4}>Sample message:</Text>
              <Text size="sm" ff="monospace">
                Standard Produce: Your order #SO-1042 is confirmed — 10cs Romaine, 5cs Roma
                Tomatoes, 3cs Green Peppers. Total $487.50. Delivery tomorrow 6-8AM. Reply
                HELP for help, STOP to cancel. Msg&amp;data rates may apply.
              </Text>
            </Alert>

            <Checkbox
              checked={false}
              readOnly
              label={
                <Text size="sm">
                  I agree to receive recurring automated SMS messages from Standard Produce
                  at the phone number on my account. I have read and accept the{' '}
                  <Anchor href="/sms-terms" target="_blank" inherit>SMS Terms of Service</Anchor>
                  {' '}and{' '}
                  <Anchor href="/privacy" target="_blank" inherit>Privacy Policy</Anchor>.
                  Consent is not a condition of purchase.
                </Text>
              }
            />

            <Group justify="space-between">
              <Text fw={500}>Receive SMS Notifications</Text>
              <Switch checked={false} disabled readOnly />
            </Group>

            <Text size="xs" c="dimmed" fs="italic">
              (Preview only — toggle disabled. Real customers see this screen after login
              and must tick the checkbox before the toggle becomes active.)
            </Text>
          </Stack>
        </Card>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Opt-in Welcome Message (sent after consent)</Title>
          <Paper withBorder p="md" bg="blue.0">
            <Text size="sm" ff="monospace">
              Standard Produce: You're subscribed to order &amp; account alerts. Msg freq
              varies (typically 2-10/wk). Msg&amp;data rates may apply. Reply HELP for help,
              STOP to cancel. portal.standardproduce.com/sms-terms
            </Text>
          </Paper>

          <Title order={4} mt="md">HELP Reply</Title>
          <Paper withBorder p="md" bg="gray.0">
            <Text size="sm" ff="monospace">
              Standard Produce SMS Help: Text your order, BAL for balance, PAY to pay.
              Msg freq varies. Msg&amp;data rates may apply. Contact: (434) 204-2184. Reply
              STOP to opt out.
            </Text>
          </Paper>

          <Title order={4} mt="md">STOP Reply (Opt-Out Confirmation)</Title>
          <Paper withBorder p="md" bg="gray.0">
            <Text size="sm" ff="monospace">
              You have successfully been unsubscribed. You will not receive any more messages
              from this number. Reply START to resubscribe.
            </Text>
          </Paper>
        </Stack>

        <Divider />

        <Text size="sm" c="dimmed">
          For full program terms see our{' '}
          <Anchor href="/sms-terms" inherit>SMS Terms of Service</Anchor>,{' '}
          <Anchor href="/privacy" inherit>Privacy Policy</Anchor>, and{' '}
          <Anchor href="/terms" inherit>Terms of Service</Anchor>. Questions? Contact
          Standard Produce Company, Charlottesville, VA — (434) 204-2184 —
          info@standardproduce.com.
        </Text>
      </Stack>
    </Container>
  );
}
